import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import type { StreamSlot } from "../../lib/schedule-document/schedule-values";
import { PlatformIcon } from "./PlatformIcon";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { cn } from "../../lib/utils";
import { hasPlatform, platformName, singlePlatforms, toggleSlotPlatform } from "../../lib/stream-platform";

const durations = Array.from({ length: 12 }, (_, index) => (index + 1) * 30);
const fieldClass = "h-7 min-w-0 rounded border border-neutral-300 bg-white px-2 text-xs outline-none focus:border-neutral-900 focus:ring-1 focus:ring-neutral-900";

type StreamSlotFormProps = {
  slot: StreamSlot; number: number; day: string; memberOnlyLabel: string;
  onChange: (patch: Partial<StreamSlot>, field?: keyof StreamSlot) => void;
  onReset: () => void; onBlur?: () => void;
  dragHandle?: ReactNode; className?: string; lockActive?: boolean;
};

export function StreamSlotForm({ slot, number, day, memberOnlyLabel, onChange, onReset, onBlur, dragHandle, className, lockActive = false }: StreamSlotFormProps) {
  const { t } = useTranslation();
  const label = (control: string) => `${day}, ${t("schedule.slot", { number })}, ${control}`;
  const leftColumn = dragHandle ? "col-start-2" : "col-start-1";
  const textColumn = dragHandle ? "col-start-3" : "col-start-2";
  const resetColumn = dragHandle ? "col-start-4" : "col-start-3";

  return (
    <div role="group" aria-label={label("")} className={cn(
      "relative grid min-w-0 grid-rows-[28px_28px] gap-x-2 gap-y-1 transition-opacity",
      dragHandle ? "grid-cols-[16px_173px_minmax(0,1fr)_24px]" : "grid-cols-[173px_minmax(0,1fr)_24px]",
      slot.active ? "opacity-100" : "opacity-60 focus-within:opacity-100 hover:opacity-100",
      className,
    )}>
      {dragHandle}
      <div className={cn(leftColumn, "row-start-1 grid grid-cols-[94px_75px] gap-1")}>
      <Button type="button" size="xs" variant="ghost"
        aria-label={label(t("schedule.toggle"))} aria-pressed={slot.active} disabled={lockActive}
        onClick={() => onChange({ active: !slot.active })}
        className={cn("h-7 w-full justify-start gap-1.5 rounded border border-neutral-200 bg-neutral-50 px-2 text-[10px]", slot.active && "bg-emerald-50 text-emerald-700 hover:bg-emerald-100")}>
        <span aria-hidden="true" className={cn("size-1.5 rounded-full", slot.active ? "bg-emerald-500" : "bg-neutral-300")} />{t("schedule.slot", { number })}
      </Button>
      <div role="group" aria-label={label(t("schedule.platform"))} className="flex shrink-0 rounded border border-neutral-300 bg-white">
        {singlePlatforms.map(platform => {
          const selected = hasPlatform(slot.platform, platform);
          const memberOnly = platform === "YT" && selected && slot.memberOnly;
          const name = memberOnly ? `YouTube · ${memberOnlyLabel || t("schedule.memberOnly")}` : platformName(platform);
          return (
            <Button key={platform} type="button" size="xs" variant="ghost"
              title={name} aria-label={label(name)} aria-pressed={selected}
              onClick={() => onChange(toggleSlotPlatform(slot, platform))}
              className={cn("relative h-[26px] flex-1 transition-none rounded-none first:rounded-l last:rounded-r p-0 has-[>svg]:p-0 focus-visible:z-10 focus-visible:ring-0 focus-visible:outline-solid focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-black", memberOnly ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800" : selected ? "bg-neutral-900 text-white hover:bg-neutral-800 hover:text-white" : "text-neutral-600")}>
              <PlatformIcon platform={platform} />
            </Button>
          );
        })}
      </div>
      </div>
      <div className={cn(leftColumn, "row-start-2 grid grid-cols-[94px_75px] gap-1")}>
      <Input type="time" aria-label={label(t("schedule.time"))}
        className="h-7 w-[94px] shrink-0 rounded bg-white px-1 py-0 font-mono text-[11px] shadow-none [&::-webkit-calendar-picker-indicator]:m-0 [&::-webkit-calendar-picker-indicator]:w-3 [&::-webkit-calendar-picker-indicator]:p-0 [&::-webkit-datetime-edit]:p-0"
        required step={900} value={slot.time}
        onBlur={onBlur}
        onChange={event => onChange({ time: event.target.value }, "time")} />
      <select aria-label={label(t("schedule.duration"))} className={cn(fieldClass, "w-[75px] shrink-0 px-1 font-mono")}
        value={slot.duration} onChange={event => onChange({ duration: Number(event.target.value) })}>
        {durations.map(minutes => <option key={minutes} value={minutes}>
          {minutes === 360 ? t("schedule.sixHoursPlus") : minutes < 60 ? t("schedule.minutes", { minutes }) : t("schedule.hoursMinutes", { hours: Math.floor(minutes / 60), minutes: String(minutes % 60).padStart(2, "0") })}
        </option>)}
      </select>
      </div>
      <Input aria-label={label(t("schedule.title"))} className={cn(textColumn, "row-start-1 h-7 rounded bg-white px-2 text-xs font-medium shadow-none placeholder:font-normal placeholder:text-neutral-400")}
        value={slot.title} placeholder={t("schedule.titlePlaceholder", { number })}
        onBlur={onBlur}
        onChange={event => onChange({ title: event.target.value, ...(event.target.value.length > 0 ? { active: true } : {}) }, "title")} />
      <Input aria-label={label(t("schedule.subheading"))}
        className={cn(textColumn, "row-start-2 h-7 rounded bg-white px-2 text-xs shadow-none placeholder:text-neutral-400")}
        value={slot.subheading ?? ""} placeholder={t("schedule.subheadingPlaceholder", { number })}
        onBlur={onBlur}
        onChange={event => onChange({ subheading: event.target.value, ...(event.target.value.length > 0 ? { active: true } : {}) }, "subheading")} />
      <Button type="button" variant="ghost" size="icon-xs" tabIndex={-1} aria-label={label(t("schedule.reset"))}
        onClick={onReset} className={cn(resetColumn, "row-span-2 row-start-1 h-full w-6 self-stretch rounded text-neutral-400 hover:bg-red-50 hover:text-red-600")}><Trash2 className="size-3.5" /></Button>
    </div>
  );
}
